import {BrowserRouter, Route, Routes, useLocation} from "react-router-dom";
import "../scss/styles2.scss";
import Layout from "../components/layout/Layout";
import Article from "../pages/Article";

import ScrollToTop from "../hooks/ScrollToTop";
import usePageTracking from "../hooks/usePageTracking";

import React, {lazy, Suspense, useContext, useEffect} from "react";
import UserContext, {UserContextProvider} from "../store/userContext";
import { SiteContextProvider } from "../store/siteContext";
import { FavouritesContextProvider } from "../store/favourites-context";

import Frontpage from "../pages/Frontpage";
import Category from "../pages/Category";
import Varsler from "../pages/min_side/Varsler";
import Favoritter from "../pages/min_side/Favoritter";
import Abonnement from "../pages/min_side/Abonnement";
import AnnonseSide from "../pages/AnnonseSide";
import AdminQuizes from "../pages/admin/edit/AdminQuizes";
import QuizCreator from "../pages/quiz/QuizCreator";
import QuizSubmissionsViewer from "../pages/quiz/QuizSubmissionsViewer";
import HavnPage from "../components/havner/HavnPage";
import ReactDOM from "react-dom/client";
import {NotificationProvider} from "../store/NotificationContext";
import NyhetsBrevKNBF from "../pages/NyhetsBrevKNBF";
import KNBFBoatForm from "../pages/KNBF-boatForm/KNBFBoatForm";
import AdminRequireLogin from "../components/other/AdminRequireLogin";
import KNBFBoatForms from "../pages/KNBF-boatForm/KNBFBoatForms";
import MagazineArchivePage from "../pages/min_side/MagazineArchivePage";
import ViewIssuu from "./pages/ViewIssuu";

const MinSide = lazy(()=>import("../pages/min_side/MinSide"));
const SjoenForAlleMagasin = lazy(()=>import("../pages/SjoenForAlleMagasin"));
const SjoenForAlleUtstillere = lazy(()=>import("../pages/SjoenForAlleUtstillere"));
const MinSideTop = lazy(()=>import( "../components/layout/MinSideTop"));
const TestPage = lazy(()=>import("../pages/TestPage"));
const TestPage2 = lazy(()=>import("../pages/TestPage2"));
const PrivacyPolicy = lazy(()=>import("../pages/PrivacyPolicy"));
const AdvertViews = lazy(()=>import("../pages/AdvertViews"));
const NyhetsbrevStatistikk = lazy(()=>import("../pages/NyhetsbrevStatistikk"));
const NyhetsbrevTakk = lazy(()=>import("../pages/NyhetsbrevTakk"));
const Abonner = lazy(()=>import("../pages/Abonner"));
const QuizViewer = lazy(()=>import("../pages/quiz/QuizViewer"));
const NotFound = lazy(()=>import("../pages/NotFound"));
const Loginbox = lazy(()=>import("../components/ui/functional/Loginbox"));
const KNBFSignupPage = lazy(()=>import("../batens_verden/pages/KNBFSignupPage"));
const Register = lazy(()=>import("../pages/Register"));
const VerifyEmail = lazy(()=>import("../pages/VerifyEmail"));
const VerifyEmailToken = lazy(()=>import("../pages/VerifyEmailToken"));
const GlemtPassord = lazy(()=>import("../pages/GlemtPassord"));
const FornyPassord = lazy(()=>import("../pages/FornyPassord"));
const VippsBuyArticle = lazy(()=>import("../pages/VippsBuyArticle"));
const VippSubscribe = lazy(()=>import("../pages/VippSubscribe"));

const Havner = lazy(()=>import("../components/havner/Havner"));

// Lazy load all admin-related components
const AdminPanel = lazy(() => import("../pages/admin/AdminPanel"));
const AdminUploadImage = lazy(() => import("../pages/admin/AdminUploadImage"));
const AdminFrontpageEdit = lazy(() => import("../pages/admin/FrontpageEdit/AdminFrontpageEdit"));
const CategoriesEdit = lazy(() => import("../pages/admin/edit/CategoriesAdmin"));
const AdvertEdit = lazy(() => import("../pages/admin/edit/AdvertsAdmin"));
const AdPlacementEdit = lazy(() => import("../pages/admin/edit/AdPlacementEdit"));
const AuthorEdit = lazy(() => import("../pages/admin/edit/AuthorsEdit"));
const AdminUsers = lazy(() => import("../pages/admin/edit/AdminUsers"));
const AdminArticles = lazy(() => import("../pages/admin/edit/AdminArticles"));
const AdminDefaultPrices = lazy(() => import("../pages/admin/edit/AdminDefaultPrices"));
const SubscriptionProductEdit = lazy(() => import("../pages/admin/edit/SubscriptionProductEdit"));
const SubscriptionPlanEdit = lazy(() => import("../pages/admin/edit/SubscriptionPlanEdit"));
const TagsEdit = lazy(() => import("../pages/admin/edit/TagsEdit"));
const ArticleEditor2 = lazy(() => import("../components/articleEdit2/ArticleEditor2"));
const VideosEdit = lazy(() => import("../pages/VideosEdit"));
const SponsorEdit = lazy(() => import("../pages/admin/edit/SponsorEdit"));
const Statistics = lazy(() => import("../pages/admin/Statistics"));
const TempAdClicks = lazy(() => import("../pages/admin/TempAdClicks"));

const AdminHarbours = lazy(() => import("../pages/admin/edit/AdminHarbours"));
const HarbourEdit = lazy(() => import("../pages/admin/HarbourEdit"));
const AdminShortNews = lazy(() => import("../pages/admin/edit/AdminShortNews"));
const ShortNewsEdit = lazy(() => import("../pages/admin/ShortNewsEdit"));


function AdminWrapper() {
	return (<Suspense fallback={<div className={'centered'}>Laster...</div>}>
		<AdminRequireLogin minRole={1}>
			<Routes>
				<Route path="/" element={<AdminPanel />} />
				<Route path="forside-rediger" element={<AdminFrontpageEdit />} />
				<Route path="kategorier" element={<CategoriesEdit />} />
				<Route path="stikkord" element={<TagsEdit />} />
				<Route path="standardpriser" element={<AdminDefaultPrices />} />
				<Route path="brukere" element={<AdminUsers />} />
				<Route path="artikler" element={<AdminArticles />} />
				<Route path="forfattere" element={<AuthorEdit />} />
				<Route path="annonsorer" element={<SponsorEdit />} />
				<Route path="annonser" element={<AdvertEdit />} />
				<Route path="annonseplasseringer" element={<AdPlacementEdit />} />
				<Route path="abonnement-produkter" element={<SubscriptionProductEdit />} />
				<Route path="abonnement-planer" element={<SubscriptionPlanEdit />} />
				<Route path="videoer" element={<VideosEdit />} />

				<Route path="quizer" element={<AdminQuizes />} />
				<Route path="quizer/ny" element={<QuizCreator isNew={true}/>} />
				<Route path="quizer/:quizId" element={<QuizCreator />} />
				<Route path="quizer/:quizId/innsendinger" element={<QuizSubmissionsViewer />} />

				<Route path="statistikk" element={<Statistics />} />

				<Route path="ny_artikkel" element={<ArticleEditor2 />} />
				<Route path="artikkel-rediger/:urlString2" element={<ArticleEditor2 />} />
				<Route path="bildeopplasting" element={<AdminUploadImage />} />

				<Route path="lenkeklikk" element={<TempAdClicks />} />
				{/*<Route path="open" element={<AdminOpen/>}/>*/}

				<Route path="/test" element={<Suspense><TestPage /></Suspense>} />
				<Route path="/test2" element={<Suspense><TestPage2 /></Suspense>} />

				<Route path="havner" element={<AdminHarbours />} />
				<Route path="havner/:harbourId" element={<HarbourEdit />} />
				<Route path="havner/ny" element={<HarbourEdit/>} />

				<Route path="kort-nyheter" element={<AdminShortNews />} />
				<Route path="kort-nyheter/:shortNewsId" element={<ShortNewsEdit />} />
				<Route path="kort-nyheter/ny" element={<ShortNewsEdit/>} />
			</Routes>
		</AdminRequireLogin>
		</Suspense>
	);
}

function MinSideWrapper() {
	const userContext = useContext(UserContext);
	if (userContext.isLoadingAuth) return <div className={'centered'}>Laster...</div>;
	if (!userContext.isLoggedIn) return <Loginbox/>

	return (<Suspense fallback={<div className={'centered'}>Laster...</div>}>
		<MinSideTop>
			<Routes>
				<Route path="/" element={<Suspense><MinSide /></Suspense>} />
				<Route path="bruker" element={<MinSide />} />
				{/*<Route path="bruker/:submenu" element={<Bruker/>}/>
					<Route path="bruker" element={<Bruker />} />*/}
				<Route path="varsler" element={<Varsler/>} />
				<Route path="favoritter" element={<Favoritter />} />
				<Route path="abonnement" element={<Abonnement />} />
				<Route path="magasin-arkiv-knbf" element={<MagazineArchivePage />} />
				<Route path="magasin-arkiv-knbf/:issuuId" element={<ViewIssuu />} />
			</Routes>
		</MinSideTop>
	</Suspense>);
}

const KNBF = () => {
	return <Routes>
		<Route path={'/nyhetsbrev/'} element={<NyhetsBrevKNBF />} />
		<Route path={'/skjema'} element={<KNBFBoatForms />} />
		<Route path={'/skjema/:formName'} element={<KNBFBoatForm />} />
	</Routes>
}

function BatensVerdenApp() {
	//const linjebanner = useDismissibleBanner(<NyhetsbrevAbonner textSize={'0.75em'} text={'Vinn CoastKey - Meld deg på vårt nyhetsbrev'}/>, "test", "linjebanner-nyhetsbrev", 4000);

	usePageTracking();
	const location = useLocation();
	useEffect(() => {
		// Track page view whenever the route changes
		try{window.gtag('config', 'G-6QBLX0EJHH', { page_path: location.pathname });}
		catch (e) {
			console.error(e);
		}
	}, [location]);

	return (
		<ScrollToTop>
			<SiteContextProvider>
				<FavouritesContextProvider>
					<Layout>
						<Routes>
							<Route path="/*" element={<Suspense><NotFound /></Suspense>} />
							<Route path="/" element={<Frontpage reFetchOnNavigation={true}/>} />
							<Route path="/forside" element={<Frontpage reFetchOnNavigation={true}/>} />
							{/*<Route path="/:videoId" element={<Frontpage/>}/>*/}


							<Route path="/nyhetsbrev-knbf" element={<NyhetsBrevKNBF />} />

							<Route path="/admin/*" element={<AdminWrapper />} />

							<Route path="/min-side/*" element={<Suspense><MinSideWrapper /></Suspense>} />

							<Route path="/ikke-funnet/:message" element={<NotFound />} />

							<Route path="/annonse-visninger/:advertId" element={<Suspense><AdvertViews /></Suspense>} />

							<Route path="/nyhetsbrev-takk" element={<Suspense><NyhetsbrevTakk/></Suspense>} />
							<Route path="/personvernerklaering" element={<Suspense><PrivacyPolicy/></Suspense>} />

							<Route path="/artikler/:urlString/" element={<Article />} />
							<Route path="/artikkel/:urlString/" element={<Article />} />
							{/*<Route path="/artikler/:pageNumber" element={<ArticlesSearch/>}/>
								<Route path="/artikler" element={<ArticlesSearch />} />*/}

							<Route path="/kategorier" element={<Category />} />
							<Route path="/kategorier/:categoryParam" element={<Category />} />
							<Route path="/kategorier/:categoryParam/:pageNumParam" element={<Category />} />

							<Route path="/login" element={<Suspense><Loginbox /></Suspense>} />
							<Route path="/login-knbf" element={<Suspense><KNBFSignupPage /></Suspense>} />
							<Route path="/registrer" element={<Suspense><Register /></Suspense>} />
							<Route path="/verifiser" element={<Suspense><VerifyEmail /></Suspense>} />
							<Route path="/verifiser/:token" element={<Suspense><VerifyEmailToken /></Suspense>} />
							<Route path="/glemtpassord" element={<Suspense><GlemtPassord /></Suspense>} />
							<Route path="/fornypassord/:token" element={<Suspense><FornyPassord /></Suspense>} />

							<Route path="resourceObj/:quizId" element={<Suspense><QuizViewer backButtonEnabled={true}/></Suspense>} />

							<Route path="/havner" element={<Suspense><Havner /></Suspense>} />
							<Route path="/havner/:harbourName" element={<Suspense><HavnPage /></Suspense>} />

							<Route path="/abonner" element={<Suspense><Abonner /></Suspense>} />

							<Route path="/vipps-artikkel" element={<Suspense><VippsBuyArticle /></Suspense>} />
							<Route path="/vipps-abonner" element={<Suspense><VippSubscribe /></Suspense>} />


							<Route path="/bv1" element={<Suspense><AnnonseSide/></Suspense>} />
							<Route path="/sjoen-for-alle" element={<Suspense><SjoenForAlleMagasin/></Suspense>} />
							<Route path="/sjoen-for-alle-utstillere" element={<Suspense><SjoenForAlleUtstillere/></Suspense>} />

							<Route path="/nyhetsbrev-statistikk" element={<Suspense><NyhetsbrevStatistikk/></Suspense>} />
						</Routes>
					</Layout>
				</FavouritesContextProvider>
			</SiteContextProvider>
		</ScrollToTop>
	);
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
	<BrowserRouter>
		<NotificationProvider>
			<UserContextProvider>
			<Routes>
				<Route
					path={'/knbf/*'}
					element={
						<Suspense fallback={<div className={'spinner'}></div>}>
							<KNBF />
						</Suspense>
					}
				/>
				<Route path="/*" element={<BatensVerdenApp />} />
			</Routes>
			</UserContextProvider>
		</NotificationProvider>
	</BrowserRouter>
);